import { FormTypes } from '@generated/graphql';

export type CombinedDocumentAndAgreement = {
  id: string;
  state: 'SAVED' | 'SIGNED' | 'PENDING';
  relatedObjectId: string;
  relatedObjectType:
    | 'USER'
    | 'ACCOUNT'
    | 'SUBACCOUNT'
    | 'GOAL'
    | 'SCHEDULED_TRANSFER'
    | 'CLIENT_GROUP';
  displayName: string;
  type: string;
  signedAt?: Date;
  signedUrl?: string;
  templateUrl?: string;
  isPrefilledPdfEnabled?: boolean;
  digitalSignatureEnabled?: boolean;
  name?: string;
  fileName?: string;
  mediaType?: string;
  uploadedAt?: Date;
  updatedAt?: Date;
  sharedClient?: boolean;
  sharedCustodian?: boolean;
  creatorId?: string;
  isFileDocument?: boolean;
  minVersion?: number;
};

export type FormAgreementFormTypeSource = {
  type?: string;
};

export const getFormAgreementFormType = (
  agreement: FormAgreementFormTypeSource,
): string | undefined => agreement.type;

export const PREVIEW_PDF_FORM_TYPES: FormTypes[] = [
  // Account-level forms (shared rrsp.pdf)
  FormTypes.Rrsp,
  FormTypes.RrspSpousal,
  FormTypes.Lira,
  FormTypes.Lrsp,
  FormTypes.Rlsp,
  FormTypes.Tfsa,
  FormTypes.Fhsa,
  FormTypes.Rrif,
  FormTypes.Lif,
  FormTypes.Lrif,
  FormTypes.Rlif,
  FormTypes.RifSpousal,
  FormTypes.RespFamilyPlan,
  FormTypes.RespSinglePlan,
  FormTypes.BeneficiaryDesignationForm,
  FormTypes.ClbForAdults,
  FormTypes.EducationSavingsGrantsApplication,
  FormTypes.LodQesi,
  FormTypes.JointClientAccountAgreement,
  // User-level forms
  FormTypes.W9,
  FormTypes.W8Ben,
  FormTypes.WaiverOfConfidentiality,
  FormTypes.DeclarationOfTaxDisclosure,
];

export const isFormAgreementForFormTypes = (
  agreement: FormAgreementFormTypeSource,
  formTypes: readonly FormTypes[],
): boolean => {
  const formType = getFormAgreementFormType(agreement);
  return formType !== undefined && formTypes.includes(formType as FormTypes);
};

export const getRelatedObjectTypeAndId = (
  object: any,
): {
  relatedObjectType?: CombinedDocumentAndAgreement['relatedObjectType'];
  relatedObjectId?: string;
} => {
  if (object.user?.id)
    return { relatedObjectType: 'USER', relatedObjectId: object.user?.id };
  if (object.account?.id)
    return {
      relatedObjectType: 'ACCOUNT',
      relatedObjectId: object.account?.id,
    };
  if (object.subAccount?.id)
    return {
      relatedObjectType: 'SUBACCOUNT',
      relatedObjectId: object.subAccount?.id,
    };
  if (object.goal?.id)
    return { relatedObjectType: 'GOAL', relatedObjectId: object.goal?.id };
  if (object.scheduledTransfer?.id)
    return {
      relatedObjectType: 'SCHEDULED_TRANSFER',
      relatedObjectId: object.scheduledTransfer?.id,
    };
  return {};
};

export const getDocumentName = (
  translateBackendString: (translatedObject: unknown) => string,
  fileItem: any,
) => {
  if (fileItem.isFileDocument) {
    return fileItem.name;
  }
  return (
    translateBackendString(fileItem.translatedDisplayName) ||
    fileItem.name ||
    fileItem.displayName ||
    fileItem.fileName ||
    fileItem.type
  );
};

export const generateColor = (state: string) => {
  if (state.toUpperCase() === 'SAVED') {
    return 'primary';
  }
  if (state.toUpperCase() === 'PENDING') {
    return 'warning';
  }
  return 'success';
};

export function objectTypeSortingWeight(
  objType: string,
  isFileDocument?: boolean,
): number {
  if (isFileDocument) return 5;
  if (objType === 'USER') return 50;
  if (objType === 'GOAL') return 40;
  if (objType === 'ACCOUNT') return 30;
  if (objType === 'SUBACCOUNT') return 20;
  if (objType === 'SCHEDULED_TRANSFER') return 10;
  return 0;
}
